import { inject, Injectable } from '@angular/core';
import { BookingType } from 'app/views/booking-list/models/booking-type';
import * as XLSX from 'xlsx';
import { Booking } from '../models/bookings.interface';
import {
  bookingCreditFlightConfiguration,
  CancelledBookingsExport,
  cancelledExportConfiguration,
  CreditFlightBookingsExport,
  defaultExportConfiguration,
  ExportConfiguration,
  ExportExtensions
} from '../models/export-booking-list.model';
import { QuickFiltersValue } from '../models/filters.model';
import { PendingBookingsExport } from './../models/export-booking-list.model';
import { BookingListFilterService } from './booking-list-filter.service';
import { BookingListService } from './booking-list.service';

@Injectable({
  providedIn: 'root'
})
export class ExportBookingListService {
  private readonly _bookingListService = inject(BookingListService);
  private readonly _bookingListFilterService = inject(BookingListFilterService);

  private exportConfiguration: ExportConfiguration = defaultExportConfiguration;

  public exportBookingList(extension: ExportExtensions, typeBookings: BookingType): void {
    this.exportConfiguration =
      typeBookings === 'WithCredit' ? bookingCreditFlightConfiguration : defaultExportConfiguration;
    const bookings = this._bookingListService.getBookingsToExport();

    let mappedBookings: PendingBookingsExport[] | CancelledBookingsExport[] | CreditFlightBookingsExport[] = [];
    if (typeBookings !== 'WithCredit') {
      if (this._bookingListFilterService.getCurrentFilters()().status === QuickFiltersValue.CancelledQuickFilters) {
        mappedBookings = this.mapToCancelledBookingsExportModel(bookings);
        this.exportConfiguration = cancelledExportConfiguration;
      } else {
        mappedBookings = this.mapToPendingBookingsExportModel(bookings);
      }
    } else {
      mappedBookings = this.mapToCreditFlightBookingsExportModel(bookings);
    }

    const workbook = this.prepareWorkbook(mappedBookings);
    this.exportWorkbook(workbook, extension);
  }

  private mapToCreditFlightBookingsExportModel(bookings: Booking[]): CreditFlightBookingsExport[] {
    return bookings.map(({ recordLocator, passengerCount, availableCredit, usedCredit, status }) => ({
      recordLocator,
      passengerCount,
      availableCredit,
      usedCredit,
      status
    }));
  }

  private mapToPendingBookingsExportModel(bookings: Booking[]): PendingBookingsExport[] {
    return bookings.map(({ recordLocator, amount, departureDate, returnDate, destination, origin }) => ({
      recordLocator,
      origin,
      destination,
      departureDate,
      returnDate,
      amount
    }));
  }

  private mapToCancelledBookingsExportModel(bookings: Booking[]): CancelledBookingsExport[] {
    return bookings.map(({ recordLocator, amount, status }) => ({
      recordLocator,
      amount,
      status
    }));
  }

  private prepareWorkbook(
    bookings: PendingBookingsExport[] | CancelledBookingsExport[] | CreditFlightBookingsExport[]
  ): XLSX.WorkBook {
    const worksheet = XLSX.utils.json_to_sheet(bookings);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.sheet_add_aoa(worksheet, [this.exportConfiguration.headers], { origin: 'A1' });
    XLSX.utils.book_append_sheet(workbook, worksheet, this.exportConfiguration.worksheetName);

    return workbook;
  }

  private exportWorkbook(workbook: XLSX.WorkBook, extension: ExportExtensions): void {
    XLSX.writeFile(workbook, `${this.exportConfiguration.fileName}.${extension}`, { compression: true });
  }
}
